import config from '../config.js';
import { resolveMediaUrl } from './mediaUrl.js';
import { DEFAULT_LOCALE, englishT } from '../locales/index.js';
import { DATE_TIME_STYLES, createFormatters } from './formatters.js';

/**
 * Frontend side of the listener notification contract (ENG-26).
 *
 * ENG-97 owns the backend and had not published its contract when this was
 * written, so everything the frontend expects is pinned here, in one place,
 * for the backend to diff against. Every reader below is tolerant of the two
 * casings the API already mixes (camelCase inline summaries, snake_case
 * rows), takes nothing on faith (a notification without an id or a parseable
 * creation time is dropped) and never infers a local default for anything the
 * backend did not send (channels, preferences, destinations).
 *
 * Which of the endpoints below are checked against a published backend, and
 * which are still only what this client expects, is marked per endpoint:
 *   [reconciled, ENG-409] — read off the shipped handler and its DTOs; a
 *                           change here has to be a change there too.
 *   everything else       — the pre-publication expectation as first written,
 *                           not yet diffed against a backend. Do not read the
 *                           unmarked blocks as agreed.
 *
 * Endpoints (all authenticated, all under `config.apiBaseUrl`):
 *   GET  /me/notifications?limit=N[&cursor=C]
 *        → { notifications: Notification[], nextCursor: string|null,
 *            unreadCount?: number, unreadCapped?: boolean }
 *        Newest first. `nextCursor` is opaque; null means the end. The
 *        unread fields are optional here because /unread-count is the
 *        authority; when present they save a round trip.
 *   GET  /me/notifications/unread-count
 *        → { count: number, capped?: boolean }
 *        `capped` means the backend stopped counting at its own ceiling; the
 *        badge then reads "99+" whatever the number.
 *   POST /me/notifications/{id}/read       → 204, or { id, readAt, unreadCount? }
 *   POST /me/notifications/read-all        → 204, or { unreadCount: 0 }
 *   GET  /me/notifications/preferences                        [reconciled, ENG-409]
 *        → { events: { liveStream: { inApp:  { enabled, available },
 *                                    email:  { enabled, available },
 *                                    push:   { enabled, available } } },
 *            locale: string|null, contentLocale: string,
 *            supportedLocales: string[], updatedAt: string|null }
 *        A matrix, not two lists: one entry per event, one cell per channel
 *        of that event, and no toggle that spans events. Only the events and
 *        channels present get a control; `available: false` (the backend has
 *        no sender for that channel) is shown at its stored state but cannot
 *        be switched.
 *   PUT  /me/notifications/preferences                        [reconciled, ENG-409]
 *        ← { liveStream: { inApp?, email?, push? }, locale? }  // bare booleans
 *        → the saved preferences, same shape as GET.
 *        The handler decodes with Go's DisallowUnknownFields, so a key it
 *        does not define is a 400 and not a field it ignores; every key sent
 *        is one a GET returned (`serializePreferences` echoes the wire keys
 *        it read). Every field is optional and an omitted one is left
 *        unchanged — which is why `locale` is never sent from here: the
 *        listener's language is owned by ENG-379's LocalePreferenceSync
 *        through `/me/locale-preference`, and both endpoints write the same
 *        row on the backend.
 *   POST /me/notifications/stream-ticket
 *        → { ticket, expiresIn }   // single use, 30 seconds (ENG-398)
 *        401 → the session is refused: do not retry. 429 / 503 → back off.
 *   GET  /me/notifications/stream?ticket=…&after=<last id>   (EventSource)
 *        event: ready         data: { unreadCount, capped }   // once, on connect
 *        event: notification  data: Notification               // id: <notification id>
 *        Opened with a ticket minted just before, never the session JWT. A
 *        ticket opens one stream, so every reconnect mints a new one, and
 *        the browser's own EventSource retry (which reuses the spent URL) is
 *        cut off by closing the source on error. `after` resumes from the
 *        last id seen; replays may repeat an event, so the client dedupes by
 *        notification id, and re-reads the first page on every reconnect
 *        for read-state changes the stream does not carry (ENG-470).
 *
 * Notification (nested or flat, either casing):
 *   { id, type: 'live_stream', createdAt, readAt: string|null,
 *     band: { id, name, profileImageURL },
 *     show: { id, title, startsAt, timezone },   // IANA zone, e.g. America/Los_Angeles
 *     destination: { route?: '/performers/12/stream', url?: 'https://…' },
 *     // Structured message (ENG-382 here, ENG-383 on the backend; additive):
 *     messageKey: 'live_stream.started',       // stable identity, never English prose
 *     schemaVersion: 1,                         // of that key's parameter shape; absent means 1
 *     params: { bandName: 'Neon Harbor', showTitle: 'Friday set',
 *               startsAt: '2026-09-15T03:00:00Z', timezone: 'America/Los_Angeles' },
 *     // Legacy rendered text, kept for old clients and for rows written
 *     // before the structured fields existed:
 *     title: 'Neon Harbor is live', body: 'Friday set · Starts Tue, Sep 15, 8:00 PM PDT',
 *     // The leading visual (ENG-890 on the backend, ENG-889 here), flat:
 *     visual: 'song' | 'money' | 'band',       // the backend picks it; never derived from type here
 *     songId, artworkUrl: 'covers/812.jpg',     // media keys; "" means none (or a hidden cover)
 *     bandImageUrl: 'bands/7.png',
 *     amountCents: 550, currency: 'usd' }      // tip.sent only, null otherwise
 *   Every delivery of a notification (page row or stream event) carries its
 *   read state: `readAt` null (or absent) means unread, so a redelivery
 *   without it reads as unread. A delivery never retracts content: `band`,
 *   `show`, `destination`, `title`, `body` and the structured message are
 *   repeated or left out, never nulled to clear them, so the client keeps
 *   what an earlier delivery of the same id said (`mergeNotifications`).
 *   `schemaVersion` is the only version field read; a row-level `version`
 *   (an ETag-style counter) is not it.
 *
 * What a row renders, in the listener's locale, is decided per row by
 * `describeNotification`, always preferring text the client can localize
 * over text the backend rendered, and never guessing:
 *   1. the structured message, when the client knows the `messageKey` and
 *      `schemaVersion` (`NOTIFICATION_MESSAGES`), every declared parameter is
 *      present and of its type, and the active locale has the translation;
 *   2. the `type` template ("{{bandName}} is live") from the `band` / `show`
 *      summaries — for a row with no `messageKey` (written before ENG-383) or
 *      whose key is the type's own message in a version this client does not
 *      know — when the summaries fill it completely;
 *   3. the backend's `title` / `body`: a `messageKey` this client does not
 *      know (whose meaning may differ from the type's template), a
 *      translation the active locale lacks, summaries that cannot fill the
 *      template;
 *   4. the template with its declared fallbacks ("A performer you follow is
 *      live"), the band name, the generic label.
 * `params` is the immutable snapshot the message was sent with: for a known
 * message, the parameters its registry entry maps to a summary field
 * (`summaries`) replace that field, so the row shows the band and show the
 * message was about. Every parameter is inserted as plain text: interpolation
 * never re-reads `{{…}}` from a value, and React escapes on render, so a band
 * called `{{bandName}}` renders as exactly that.
 *
 * Errors follow fetchWrapper: `{ message, status, statusText }`. A 404 or 501
 * from any of these means the backend has not shipped ENG-97 yet; the UI then
 * marks live alerts unavailable and carries on with the request/tip activity
 * it already had. The preferences route does exist on the backend, so a 404
 * there means the deployment predates it rather than that nothing was ever
 * built; it is still reported as "not available yet", because that is what it
 * is for the listener and there is nothing for them to retry. These paths are
 * exempt from fetchWrapper's 401/403
 * auto-logout (see AUTO_LOGOUT_EXEMPT_PATHS there): they are requested on
 * every signed-in page, so a refusal is reported by the feed as unauthorized
 * rather than ending the session.
 *
 * Text (ENG-386): every helper here that produces something a listener reads
 * takes the component's `t` (and, where it formats a date or number, the
 * `locale`) as its last parameters and falls back to English (`englishT`) —
 * `describeNotification`, `notificationLabel`, `toActivityItems`,
 * `mergeActivity`, `normalizePreferences`, `unreadBadgeLabel`. The `*_TEXT`
 * exports are the English copy of the same catalog messages, kept for
 * callers and tests that compare against English.
 */

export const NOTIFICATION_TYPES = Object.freeze({ LIVE_STREAM: 'live_stream' });

export const FEED_PAGE_SIZE = 20;

/** Largest number the badge spells out; anything beyond (or `capped`) is "99+". */
export const UNREAD_BADGE_MAX = 99;

export const NOTIFICATION_CHANNELS = Object.freeze({ IN_APP: 'in_app', EMAIL: 'email', PUSH: 'push' });

/** Catalog keys of the client's labels for channels the backend lists without a `label`. */
const CHANNEL_LABEL_KEYS = {
  [NOTIFICATION_CHANNELS.IN_APP]: 'notifications.preferences.channels.inApp',
  [NOTIFICATION_CHANNELS.EMAIL]: 'notifications.preferences.channels.email',
  [NOTIFICATION_CHANNELS.PUSH]: 'notifications.preferences.channels.push',
};

const EVENT_LABEL_KEYS = {
  [NOTIFICATION_TYPES.LIVE_STREAM]: 'notifications.preferences.events.liveStream',
};

/**
 * The preferences payload keys its events and channels in camelCase, while
 * this client names the same things in the snake_case of `NOTIFICATION_TYPES`
 * and `NOTIFICATION_CHANNELS` (the casing the rest of the API uses for a
 * notification's `type`). These translate the wire keys to those ids; a key
 * neither table knows keeps its own spelling as its id, so an event or channel
 * the backend adds still renders and still saves.
 */
const EVENT_IDS_BY_WIRE_KEY = { liveStream: NOTIFICATION_TYPES.LIVE_STREAM };
const CHANNEL_IDS_BY_WIRE_KEY = {
  inApp: NOTIFICATION_CHANNELS.IN_APP,
  email: NOTIFICATION_CHANNELS.EMAIL,
  push: NOTIFICATION_CHANNELS.PUSH,
};

/**
 * How a structured parameter is typed, which decides how it is checked and
 * formatted before interpolation (ENG-382):
 *   text      a non-empty string, inserted as-is (user or performer content);
 *             a number is not text
 *   count     an integer, left as a number so `t` selects the plural form on
 *             it; `{{count}}` interpolates its plain digits, unformatted, as
 *             every `count` in the catalog does — a quantity to *show* in the
 *             locale's digits and grouping is a `number` parameter
 *   number    a finite number, formatted with the locale's digits and grouping
 *   datetime  a date-time, formatted like a show start in the `timezone`
 *             parameter (the viewer's zone without one)
 */
export const MESSAGE_PARAM_TYPES = Object.freeze({ TEXT: 'text', COUNT: 'count', NUMBER: 'number', DATETIME: 'datetime' });

/**
 * The structured messages this client can render, by backend `messageKey`
 * (the identities ENG-383 persists; diff against this when adding one):
 *   versions   the parameter-schema versions understood here; any other
 *              version cannot be rendered from `params`
 *   key        the catalog message
 *   params     the parameters the message interpolates, by type; a missing or
 *              mistyped one means the message cannot be rendered truthfully
 *   summaries  parameter → the ENG-97 summary field it snapshots
 *              (`band.name`, `show.startsAt`, …). Both ways: a sent parameter
 *              replaces that field on the row, and the field stands in for a
 *              parameter the backend left out of `params` (or, for the
 *              `type` template of a legacy row, for all of them). Only the
 *              fields named here are ever touched by `params`.
 *   fallbacks  catalog key rendered for a parameter the `type` template of a
 *              legacy row cannot fill from the summaries; a parameter without
 *              one leaves the template unrenderable
 * The catalog keys must exist in en-US (the catalog gate scans this table).
 */
export const NOTIFICATION_MESSAGES = Object.freeze({
  'live_stream.started': Object.freeze({
    versions: Object.freeze([1]),
    key: 'notifications.messages.liveStreamStarted',
    params: Object.freeze({ bandName: MESSAGE_PARAM_TYPES.TEXT }),
    summaries: Object.freeze({ bandName: 'band.name', showTitle: 'show.title', startsAt: 'show.startsAt', timezone: 'show.timezone' }),
    fallbacks: Object.freeze({ bandName: 'notifications.label.performerFallback' }),
  }),
});

/** The message a legacy row of each `type` is rendered with when it carries no title of its own. */
const TYPE_MESSAGE_KEYS = {
  [NOTIFICATION_TYPES.LIVE_STREAM]: 'live_stream.started',
};

/**
 * Whether ENG-97 has published a web-push contract (VAPID key endpoint,
 * subscription registration and the service-worker requirements). It has
 * not, so the push channel is shown as unavailable and the browser is never
 * asked for notification permission. Flip this only alongside that contract.
 */
export const WEB_PUSH_CONTRACT_AVAILABLE = false;

/** Catalog key per `describePushAvailability` reason. */
export const PUSH_UNAVAILABLE_KEYS = Object.freeze({
  'no-contract': 'notifications.preferences.push.noContract',
  'browser-unsupported': 'notifications.preferences.push.browserUnsupported',
  'permission-denied': 'notifications.preferences.push.permissionDenied',
});

/** English copy of `PUSH_UNAVAILABLE_KEYS`, for tests and callers without a `t`. */
export const PUSH_UNAVAILABLE_TEXT = Object.fromEntries(
  Object.entries(PUSH_UNAVAILABLE_KEYS).map(([reason, key]) => [reason, englishT(key)])
);

export const NOTIFICATIONS_ENDPOINTS = Object.freeze({
  feed: () => `${config.apiBaseUrl}/me/notifications`,
  unreadCount: () => `${config.apiBaseUrl}/me/notifications/unread-count`,
  markRead: (id) => `${config.apiBaseUrl}/me/notifications/${encodeURIComponent(id)}/read`,
  markAllRead: () => `${config.apiBaseUrl}/me/notifications/read-all`,
  preferences: () => `${config.apiBaseUrl}/me/notifications/preferences`,
  // The live feed is a server-sent-events stream (ENG-398, ENG-470). It is
  // opened with a single-use ticket minted just before, never with the
  // session JWT: an EventSource cannot send the Authorization header, and a
  // JWT in a URL ends up in access logs. `after` is the last notification id
  // seen, so a new connection resumes where the last one stopped.
  streamTicket: () => `${config.apiBaseUrl}/me/notifications/stream-ticket`,
  stream: (ticket, after) => {
    const params = new URLSearchParams({ ticket });
    if (after) params.set('after', after);
    return `${config.apiBaseUrl}/me/notifications/stream?${params}`;
  },
});

const parseDate = (value) => {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * The backend names where a notification goes; the frontend only decides how
 * to get there. A route is an app path (`/performers/12/stream`); a URL is
 * anything else. Same-origin URLs become routes so the SPA can navigate them
 * without a reload. Anything that is not an absolute path or an http(s) URL
 * — `javascript:`, protocol-relative `//host`, a path with a backslash
 * (`/\host` resolves cross-origin in the WHATWG parser), empty strings —
 * yields null and the notification renders without a link rather than with
 * a bad one.
 */
export const normalizeDestination = (destination) => {
  if (!destination) return null;
  // A bare string is a route when it is a path, otherwise a URL to be vetted.
  const raw = typeof destination === 'string'
    ? (destination.trim().startsWith('/') ? { route: destination } : { url: destination })
    : destination;
  const route = raw.route ?? raw.path ?? null;
  const url = raw.url ?? raw.href ?? null;

  const asRoute = (value) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed.startsWith('/') || trimmed.startsWith('//') || trimmed.includes('\\')) return null;
    return { kind: 'route', to: trimmed };
  };

  const fromRoute = asRoute(route);
  if (fromRoute) return fromRoute;

  if (typeof url === 'string' && url.trim()) {
    const trimmed = url.trim();
    if (trimmed.startsWith('/')) return asRoute(trimmed);
    let parsed;
    try {
      parsed = new URL(trimmed);
    } catch {
      return null;
    }
    if (!/^https?:$/.test(parsed.protocol)) return null;
    const clientOrigin = (() => {
      try {
        return config.clientBaseUrl ? new URL(config.clientBaseUrl).origin : null;
      } catch {
        return null;
      }
    })();
    if (clientOrigin && parsed.origin === clientOrigin) {
      return { kind: 'route', to: `${parsed.pathname}${parsed.search}${parsed.hash}` };
    }
    return { kind: 'url', href: parsed.href };
  }
  return null;
};

/** A non-empty string, else null. */
const text = (value) => (typeof value === 'string' && value.trim() ? value : null);

/**
 * `params`, either casing → a plain object of scalar parameters (strings,
 * finite numbers, booleans; anything else is not a parameter), or null when
 * the backend sent none. Names follow the placeholder rules, so a hostile
 * `__proto__` or `{{x}}` entry is dropped rather than read.
 */
const normalizeMessageParams = (raw) => {
  const params = raw.params ?? raw.parameters ?? raw.messageParams ?? raw.message_params ?? null;
  if (!params || typeof params !== 'object' || Array.isArray(params)) return null;
  const normalized = {};
  for (const [name, value] of Object.entries(params)) {
    if (!/^[A-Za-z][A-Za-z0-9]*$/.test(name)) continue;
    if (typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value)) || typeof value === 'boolean') {
      normalized[name] = value;
    }
  }
  return normalized;
};

/**
 * `schemaVersion`, either casing: absent means 1; anything that is not a
 * positive integer is unrecognised (null). A bare `version` on the row is
 * not read — that is a row version, not the message's.
 */
const normalizeSchemaVersion = (raw) => {
  const version = raw.schemaVersion ?? raw.schema_version;
  if (version === undefined || version === null || version === '') return 1;
  const number = Number(version);
  return Number.isInteger(number) && number >= 1 ? number : null;
};

/**
 * The summary fields a known message's `params` replace, by path
 * (`{ 'band.name': 'Neon Harbor' }`): only the parameters its registry entry
 * maps in `summaries`, and only when the key and version are ones this client
 * knows — the parameters of an unknown message are not a contract this client
 * can read, so they leave the summaries alone.
 */
const summaryOverrides = (messageKey, schemaVersion, params) => {
  const spec = messageKey ? NOTIFICATION_MESSAGES[messageKey] : null;
  if (!spec?.summaries || !params || !spec.versions.includes(schemaVersion)) return {};
  const overrides = {};
  for (const [name, path] of Object.entries(spec.summaries)) {
    if (params[name] != null) overrides[path] = params[name];
  }
  return overrides;
};

const normalizeBandSummary = (notification, overrides) => {
  const band = notification.band ?? null;
  const id = band?.id ?? band?.ID ?? notification.bandId ?? notification.band_id ?? null;
  const name = text(overrides['band.name']) ?? band?.name ?? band?.Name ?? notification.bandName ?? notification.band_name ?? null;
  if (id == null && !name) return null;
  return {
    id,
    // No name: `describeNotification` fills in the localized "A performer you follow".
    name: name || null,
    profileImageURL: resolveMediaUrl(
      band?.profileImageURL ?? band?.ProfileImageURL ?? band?.profile_image_url ?? notification.bandProfileImageURL ??
        (text(notification.bandImageUrl ?? notification.band_image_url) || null)
    ),
  };
};

const normalizeShowSummary = (notification, overrides) => {
  const show = notification.show ?? null;
  const id = show?.id ?? show?.ID ?? notification.showId ?? notification.show_id ?? null;
  const title = text(overrides['show.title']) ?? show?.title ?? show?.Title ?? notification.showTitle ?? notification.show_title ?? null;
  const startsAt = parseDate(overrides['show.startsAt']) ?? parseDate(show?.startsAt ?? show?.starts_at ?? notification.startsAt ?? notification.starts_at);
  const timezone = text(overrides['show.timezone']) ?? show?.timezone ?? show?.timeZone ?? notification.timezone ?? notification.time_zone ?? null;
  if (id == null && !title && !startsAt) return null;
  return { id, title: title || null, startsAt, timezone: typeof timezone === 'string' && timezone ? timezone : null };
};

/** The visuals a notification can lead with (ENG-889/890); the backend picks one per row. */
export const NOTIFICATION_VISUALS = Object.freeze({ SONG: 'song', MONEY: 'money', BAND: 'band' });

const VISUAL_KINDS = new Set(Object.values(NOTIFICATION_VISUALS));

const wholeNumber = (value) => {
  const n = typeof value === 'string' && value.trim() ? Number(value) : value;
  return typeof n === 'number' && Number.isInteger(n) ? n : null;
};

/**
 * The row's visual as the backend sent it (ENG-890), or null when it sent
 * none (a backend from before ENG-890): which kind, and the data that kind
 * draws. Media keys are resolved here; an empty key ("" means none) is null.
 * The client never derives the kind from `type`: the backend owns that map.
 */
const normalizeVisual = (raw) => {
  const kind = raw.visual;
  if (!VISUAL_KINDS.has(kind)) return null;
  const currency = text(raw.currency);
  return {
    kind,
    artworkUrl: resolveMediaUrl(text(raw.artworkUrl ?? raw.artwork_url)),
    bandImageUrl: resolveMediaUrl(text(raw.bandImageUrl ?? raw.band_image_url)),
    amountCents: wholeNumber(raw.amountCents ?? raw.amount_cents),
    currency: currency ? currency.toLowerCase() : null,
    songId: raw.songId ?? raw.song_id ?? null,
  };
};

/**
 * An amount in cents as the tile shows it, matching the backend's `usd()`:
 * "$5", "$5.50". Only USD is charged today; another currency is formatted by
 * the locale, with cents only when there are some.
 */
export const formatNotificationAmount = (cents, currency = 'usd', locale = DEFAULT_LOCALE) => {
  if (!Number.isInteger(cents)) return null;
  const code = (currency || 'usd').toLowerCase();
  if (code === 'usd') {
    const sign = cents < 0 ? '-' : '';
    const abs = Math.abs(cents);
    const dollars = Math.floor(abs / 100);
    const rest = abs % 100;
    return rest === 0 ? `${sign}$${dollars}` : `${sign}$${dollars}.${String(rest).padStart(2, '0')}`;
  }
  try {
    const digits = cents % 100 === 0 ? 0 : 2;
    return createFormatters(locale).number(cents / 100, {
      style: 'currency',
      currency: code.toUpperCase(),
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }) || null;
  } catch {
    return null;
  }
};

/**
 * What a feed row's leading tile shows (ENG-889):
 *   { kind: 'money', amount }                      the stylized amount
 *   { kind: 'song' | 'band', sources: [url, …] }   images to try in order; the
 *                                                  tile falls back to a
 *                                                  placeholder when all fail
 * A song tries its cover, then the band image; a band its image. Money
 * without a formattable amount shows the band instead. A row the backend sent
 * no visual for (before ENG-890) shows the band image it may carry.
 */
export const notificationTile = (notification, locale = DEFAULT_LOCALE) => {
  const visual = notification?.visual ?? null;
  const bandImage = visual?.bandImageUrl ?? notification?.band?.profileImageURL ?? null;
  if (visual?.kind === NOTIFICATION_VISUALS.MONEY) {
    const amount = formatNotificationAmount(visual.amountCents, visual.currency ?? 'usd', locale);
    if (amount) return { kind: NOTIFICATION_VISUALS.MONEY, amount };
  }
  if (visual?.kind === NOTIFICATION_VISUALS.SONG) {
    return { kind: NOTIFICATION_VISUALS.SONG, sources: [visual.artworkUrl, bandImage].filter(Boolean) };
  }
  return { kind: NOTIFICATION_VISUALS.BAND, sources: bandImage ? [bandImage] : [] };
};

/**
 * One backend notification → what the inbox renders, or null when it cannot
 * be rendered truthfully (no id to dedupe/mark by, or no creation time to
 * order by). `read` is derived from `readAt` (or an explicit `read` flag);
 * a missing read state means unread — the backend never has to say "unread".
 * The structured fields (`messageKey`, `schemaVersion`, `params`) and the
 * legacy text (`title`, `body`) are carried as sent, both null when absent;
 * `describeNotification` decides between them at render time, in the
 * listener's locale.
 */
export const normalizeNotification = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.id ?? raw.ID ?? raw.notificationId ?? raw.notification_id;
  const createdAt = parseDate(raw.createdAt ?? raw.created_at ?? raw.sentAt ?? raw.sent_at);
  if (id == null || String(id) === '' || !createdAt) return null;

  const readAt = parseDate(raw.readAt ?? raw.read_at);
  const read = readAt ? true : typeof raw.read === 'boolean' ? raw.read : Boolean(raw.isRead ?? raw.is_read);
  const messageKey = text(raw.messageKey ?? raw.message_key);
  const schemaVersion = messageKey ? normalizeSchemaVersion(raw) : null;
  const params = normalizeMessageParams(raw);
  const overrides = summaryOverrides(messageKey, schemaVersion, params);
  const band = normalizeBandSummary(raw, overrides);
  const show = normalizeShowSummary(raw, overrides);
  const type = raw.type ?? raw.kind ?? NOTIFICATION_TYPES.LIVE_STREAM;

  return {
    id: String(id),
    type,
    createdAt,
    read,
    readAt: read ? readAt : null,
    band,
    show,
    destination: normalizeDestination(
      raw.destination ?? raw.destinationRoute ?? raw.destination_route ?? raw.destinationUrl ?? raw.destination_url ?? raw.link ?? null
    ),
    messageKey,
    schemaVersion,
    params,
    // The backend's own rendering, for messages this client cannot render itself.
    title: text(raw.title ?? raw.message),
    body: text(raw.body),
    // The leading visual (ENG-889), null when the backend sent none.
    visual: normalizeVisual(raw),
  };
};

/**
 * One typed parameter, checked and formatted for interpolation in `locale`;
 * null when the value is missing or not of the declared type.
 */
const formatMessageParam = (type, value, params, locale) => {
  switch (type) {
    case MESSAGE_PARAM_TYPES.TEXT:
      return text(value);
    case MESSAGE_PARAM_TYPES.COUNT:
      return typeof value === 'number' && Number.isInteger(value) ? value : null;
    case MESSAGE_PARAM_TYPES.NUMBER:
      return typeof value === 'number' && Number.isFinite(value) ? createFormatters(locale).number(value) : null;
    case MESSAGE_PARAM_TYPES.DATETIME:
      return typeof value === 'string' || value instanceof Date ? formatShowStart(value, text(params?.timezone), locale) : null;
    default:
      return null;
  }
};

/** The summary field a registry entry maps a parameter to (`band.name` → `notification.band.name`), or undefined. */
const summaryValue = (notification, spec, name) =>
  spec.summaries?.[name] ? spec.summaries[name].split('.').reduce((value, segment) => value?.[segment], notification) : undefined;

/** Whether the active locale itself has the message (a translator without `has`, a plain function, is trusted to). */
const hasTranslation = (t, key, params) => typeof t.has !== 'function' || t.has(key, params);

/**
 * The catalog message a structured notification renders with, `{ key,
 * params }` for `t()` with every parameter checked against its type and
 * formatted for `locale`, or null when it cannot be rendered from the
 * structured fields: no `messageKey`, a key or schema version not in
 * `messages`, a parameter missing (from `params` and from the summary field
 * it snapshots) or mistyped, or the active locale lacking the translation.
 * `messages` is the registry, `NOTIFICATION_MESSAGES` unless a caller has a
 * reason to look elsewhere.
 */
export const notificationMessage = (notification, { t = englishT, locale = DEFAULT_LOCALE, messages = NOTIFICATION_MESSAGES } = {}) => {
  if (!notification?.messageKey) return null;
  const spec = messages[notification.messageKey];
  // `normalizeNotification` turns an absent version into 1 and an unrecognised one into null.
  const version = notification.schemaVersion === undefined ? 1 : notification.schemaVersion;
  if (!spec || !spec.versions.includes(version)) return null;
  const params = {};
  for (const [name, type] of Object.entries(spec.params ?? {})) {
    const value = formatMessageParam(type, notification.params?.[name] ?? summaryValue(notification, spec, name), notification.params, locale);
    if (value === null) return null;
    params[name] = value;
  }
  if (!hasTranslation(t, spec.key, params)) return null;
  return { key: spec.key, params };
};

/**
 * The `type` template of a row the structured path could not render: the
 * type's own message with every parameter read from the summaries, as
 * `{ key, params, complete }` — `complete` false when a parameter had to
 * come from its registry fallback ("A performer you follow"). Null when the
 * type has no template, when the row names a *different* message (an unknown
 * `messageKey` may mean something the template does not — the backend's
 * title is the truthful text then), when a parameter has neither a summary
 * value nor a fallback, or when the active locale lacks the translation.
 */
const typeTemplate = (notification, t, locale) => {
  const messageKey = TYPE_MESSAGE_KEYS[notification.type];
  if (!messageKey || (notification.messageKey && notification.messageKey !== messageKey)) return null;
  const spec = NOTIFICATION_MESSAGES[messageKey];
  const params = {};
  let complete = true;
  for (const [name, type] of Object.entries(spec.params ?? {})) {
    let value = formatMessageParam(type, summaryValue(notification, spec, name), notification.params, locale);
    if (value === null) {
      const fallbackKey = spec.fallbacks?.[name];
      if (!fallbackKey) return null;
      value = t(fallbackKey);
      complete = false;
    }
    params[name] = value;
  }
  if (!hasTranslation(t, spec.key, params)) return null;
  return { key: spec.key, params, complete };
};

/**
 * Show start in the show's own timezone ("Tue, Sep 15, 8:00 PM PDT"). An
 * unknown or invalid zone falls back to the viewer's (the formatter drops
 * it); null without a start.
 */
export const formatShowStart = (startsAt, timezone, locale = undefined) =>
  createFormatters(locale).dateTime(startsAt, timezone ? { ...DATE_TIME_STYLES.showStart, timeZone: timezone } : 'showStart') || null;

/** The show title and localized start of a notification's show, or null without either. */
const showDetail = (notification, t, locale) => {
  const startText = formatShowStart(notification.show?.startsAt, notification.show?.timezone, locale);
  const parts = [notification.show?.title, startText ? t('notifications.row.starts', { time: startText }) : null].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
};

/**
 * What a row shows for a notification in the given locale (English without a
 * `t`): `{ label, detail, source }`, localized text first, the backend's
 * rendering only where the client cannot render truthfully (the order is
 * spelled out in the module header).
 *   label   the structured message → the `type` template filled from the
 *           summaries → the backend's `title` → the template with its
 *           fallbacks → the band name → "Notification"
 *   detail  with a localized label, the show title and start formatted here,
 *           else the backend's `body` (its language, but better than losing
 *           it); with the backend's label, its `body`, else the show; null
 *           when there is nothing to say
 *   source  'structured' | 'template' | 'legacy' | 'fallback' — which of the
 *           above produced the label
 */
export const describeNotification = (notification, t = englishT, locale = DEFAULT_LOCALE) => {
  if (!notification) return { label: '', detail: null, source: 'fallback' };
  const localized = (message, source) => ({
    label: t(message.key, message.params),
    detail: showDetail(notification, t, locale) ?? notification.body,
    source,
  });
  const structured = notificationMessage(notification, { t, locale });
  if (structured) return localized(structured, 'structured');
  const template = typeTemplate(notification, t, locale);
  if (template?.complete) return localized(template, 'template');
  const detail = notification.body ?? showDetail(notification, t, locale);
  if (notification.title) return { label: notification.title, detail, source: 'legacy' };
  if (template) return localized(template, 'template');
  return { label: notification.band?.name || t('notifications.label.fallback'), detail, source: 'fallback' };
};

/** Human label for a notification row in the given locale (English without a `t`); see `describeNotification`. */
export const notificationLabel = (notification, t = englishT, locale = DEFAULT_LOCALE) => describeNotification(notification, t, locale).label;

const byNewestFirst = (a, b) => (b.createdAt - a.createdAt) || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0);

/** Fields that describe the notification itself; a mention that leaves one out has not lost it. */
const CONTENT_FIELDS = ['band', 'show', 'destination', 'title', 'body', 'visual'];

/** The structured message, kept together: a key's params mean nothing under another key or version. */
const MESSAGE_FIELDS = ['messageKey', 'schemaVersion', 'params'];

/**
 * A later mention of an id over an earlier one: the later's state (read,
 * readAt — every delivery carries it) and everything it does say, with the
 * content the later mention left out kept from the earlier — a stream event
 * that carries only the legacy title must not strip the structured fields a
 * page delivered, and a page that has since gained them must not be undone
 * by a redelivery without them (ENG-382, mixed old/new pages and live
 * events). The contract has no way to clear content (a null is "left out",
 * see the header), so nothing here is ever emptied. The structured message
 * moves as a unit: a mention without a `messageKey` keeps the earlier key,
 * version and params together, and one that names the same key and version
 * without params inherits them; a different key or version brings its own.
 */
const mergeNotificationEntries = (previous, next) => {
  if (!previous) return next;
  const merged = { ...previous, ...next };
  for (const field of CONTENT_FIELDS) {
    if (next[field] == null && previous[field] != null) merged[field] = previous[field];
  }
  if (!next.messageKey && previous.messageKey) {
    for (const field of MESSAGE_FIELDS) merged[field] = previous[field];
  } else if (next.params == null && next.messageKey === previous.messageKey && next.schemaVersion === previous.schemaVersion) {
    merged.params = previous.params;
  }
  return merged;
};

/**
 * Merge notifications by id, newest first. A later mention of an id wins,
 * so a redelivered live event or a re-read page updates in place instead of
 * duplicating (content the later mention leaves out is kept, see
 * mergeNotificationEntries). Accepts raw or already-normalised entries;
 * unrenderable ones are dropped.
 */
export const mergeNotifications = (...lists) => {
  const byId = new Map();
  for (const list of lists) {
    for (const entry of Array.isArray(list) ? list : []) {
      const normalized = entry && entry.createdAt instanceof Date ? entry : normalizeNotification(entry);
      if (normalized) byId.set(normalized.id, mergeNotificationEntries(byId.get(normalized.id), normalized));
    }
  }
  return [...byId.values()].sort(byNewestFirst);
};

/** `GET /me/notifications` → `{ notifications, nextCursor, unread }` (unread null when not sent). */
export const normalizeFeed = (response) => {
  const list = Array.isArray(response) ? response : response?.notifications ?? response?.items ?? [];
  const nextCursor = Array.isArray(response) ? null : response?.nextCursor ?? response?.next_cursor ?? null;
  const unreadRaw = Array.isArray(response) ? undefined : response?.unreadCount ?? response?.unread_count;
  return {
    notifications: mergeNotifications(list),
    nextCursor: typeof nextCursor === 'string' && nextCursor ? nextCursor : null,
    unread: unreadRaw === undefined ? null : normalizeUnreadCount({ count: unreadRaw, capped: response?.unreadCapped ?? response?.unread_capped }),
  };
};

/** `GET /me/notifications/unread-count` (or a bare number) → `{ count, capped }`. */
export const normalizeUnreadCount = (response) => {
  const raw = typeof response === 'number' ? response : response?.count ?? response?.unreadCount ?? response?.unread_count ?? 0;
  const count = Math.max(0, Math.floor(Number(raw) || 0));
  const capped = typeof response === 'object' && response !== null ? Boolean(response.capped) : false;
  return { count, capped };
};

/** Badge text: null when nothing is unread, "99+" past the cap or when the backend capped. */
export const formatUnreadBadge = ({ count = 0, capped = false } = {}) => {
  if (count <= 0 && !capped) return null;
  if (capped || count > UNREAD_BADGE_MAX) return `${UNREAD_BADGE_MAX}+`;
  return String(count);
};

/**
 * Catalog message for the bell's accessible name, spelling the badge out:
 * `{ key, params }` for `t()` (ENG-387). Counts past the cap use the capped
 * message rather than a plural form.
 */
export const unreadBadgeMessage = (unread) => {
  const badge = formatUnreadBadge(unread);
  if (!badge) return { key: 'navigation.notifications.bell', params: {} };
  if (badge.endsWith('+')) return { key: 'navigation.notifications.bellUnreadCapped', params: { max: UNREAD_BADGE_MAX } };
  return { key: 'navigation.notifications.bellUnread', params: { count: Number(badge) } };
};

/** Accessible name for the bell in the given locale (English without a `t`). */
export const unreadBadgeLabel = (unread, t = englishT) => {
  const { key, params } = unreadBadgeMessage(unread);
  return t(key, params);
};

/** 404/501: the backend has not shipped ENG-97 (or this part of it). */
export const isNotificationsUnavailableError = (error) => [404, 501].includes(Number(error?.status));

/** 401/403 from fetchWrapper; the notification paths are exempt from its auto-logout, so the caller sees these. */
export const isUnauthorizedError = (error) => [401, 403].includes(Number(error?.status));

/**
 * Why browser push cannot be switched on here, or null when it can. The
 * reasons are checked in the order the listener can do anything about them:
 * no contract (nothing to do), no browser support, permission denied (fixable
 * in browser settings). `Notification.permission` is only read, never
 * requested — asking is off the table until ENG-97 defines push.
 */
export const describePushAvailability = ({
  contractAvailable = WEB_PUSH_CONTRACT_AVAILABLE,
  browser = typeof window !== 'undefined' ? window : undefined,
} = {}) => {
  if (!contractAvailable) return 'no-contract';
  if (!browser || !('Notification' in browser) || !('serviceWorker' in (browser.navigator ?? {})) || !('PushManager' in browser)) {
    return 'browser-unsupported';
  }
  if (browser.Notification.permission === 'denied') return 'permission-denied';
  return null;
};

/**
 * A wire key with no catalog label, made readable: `liveStream` → "live
 * stream", `sms` → "sms". Only ever reached for an event or channel this
 * client does not know, which is the one case there is nothing to translate.
 */
const readableWireKey = (wireKey) =>
  String(wireKey).replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').toLowerCase();

/**
 * The parts of a switch that do not depend on its state: the frontend id for
 * a wire key, the label to show now, and `labelKey` when that came from the
 * catalog, so a component can re-render it in a locale chosen later. The
 * backend sends no labels for preferences (a cell is `{ enabled, available }`
 * and nothing else), so the catalog is the only source there is.
 */
const toggleIdentity = (wireKey, idsByWireKey, labelKeys, t) => {
  // Own keys only: the backend names these, and `constructor` or `toString`
  // must read as an unknown key rather than as something off Object.prototype.
  const id = Object.hasOwn(idsByWireKey, wireKey) ? idsByWireKey[wireKey] : String(wireKey);
  const labelKey = Object.hasOwn(labelKeys, id) ? labelKeys[id] : null;
  return { id, wireKey: String(wireKey), label: labelKey ? t(labelKey) : readableWireKey(wireKey), labelKey };
};

/**
 * One `{ enabled, available }` cell as a switch. A value that is not a cell
 * is dropped rather than read as "off": the panel shows the backend's state
 * or nothing, never a local default standing in for it. The push channel
 * additionally carries `unavailableReason` from describePushAvailability(),
 * since even a backend-supported push channel cannot be used without the
 * web-push contract or the browser's cooperation.
 */
const normalizeChannelCell = (wireKey, cell, pushOptions, t) => {
  if (!cell || typeof cell !== 'object' || typeof cell.enabled !== 'boolean') return null;
  const identity = toggleIdentity(wireKey, CHANNEL_IDS_BY_WIRE_KEY, CHANNEL_LABEL_KEYS, t);
  const available = cell.available !== false;
  const channel = { ...identity, enabled: cell.enabled };
  if (identity.id !== NOTIFICATION_CHANNELS.PUSH) return { ...channel, available, unavailableReason: available ? null : 'backend' };
  const reason = describePushAvailability(pushOptions);
  return { ...channel, available: available && !reason, unavailableReason: !available ? 'backend' : reason };
};

/** One event of the matrix: its own label, and the channels it can be delivered on. */
const normalizeEventEntry = (wireKey, value, pushOptions, t) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const channels = Object.entries(value)
    .map(([channelKey, cell]) => normalizeChannelCell(channelKey, cell, pushOptions, t))
    .filter(Boolean);
  if (channels.length === 0) return null;
  return { ...toggleIdentity(wireKey, EVENT_IDS_BY_WIRE_KEY, EVENT_LABEL_KEYS, t), channels };
};

/**
 * `GET /me/notifications/preferences` → `{ events: [{ …, channels: [] }] }`:
 * the backend's matrix, one group per event and one switch per channel of
 * that event, in the order it sent them. There is no toggle above a cell on
 * the backend — no global "email off", no "mute live streams" — so there is
 * none here either; an event or channel the response leaves out simply does
 * not exist. Client-side labels are resolved with `t` (English without one).
 *
 * `locale` / `contentLocale` / `supportedLocales` / `updatedAt` are read off
 * the same response but deliberately not carried: the listener's language is
 * ENG-379's, set through `/me/locale-preference`, and this panel neither
 * shows nor sends it.
 */
export const normalizePreferences = (response, pushOptions, t = englishT) => {
  const events = response?.events;
  if (!events || typeof events !== 'object' || Array.isArray(events)) return { events: [] };
  return {
    events: Object.entries(events)
      .map(([wireKey, value]) => normalizeEventEntry(wireKey, value, pushOptions, t))
      .filter(Boolean),
  };
};

/**
 * `PUT /me/notifications/preferences` body: the toggles under the wire keys
 * they were read with, and nothing else. Echoing the keys rather than
 * rebuilding them from ids is what keeps the body decodable — the handler
 * refuses an unknown field outright — and is why an event or channel this
 * client has no name for still saves. `locale` is never sent, so the backend
 * leaves the language alone.
 */
export const serializePreferences = (preferences) =>
  Object.fromEntries(
    (preferences?.events ?? []).map((event) => [
      event.wireKey,
      Object.fromEntries((event.channels ?? []).map((channel) => [channel.wireKey, Boolean(channel.enabled)])),
    ])
  );

/**
 * Feed rows for a home-card style list: live-stream notifications become
 * `{ id, kind: 'live_stream', label, detail, at, read, band, destination, show }`
 * next to the existing request/tip items, newest first. Live rows keep the
 * backend id under `notificationId` so a click can mark exactly that one read.
 * `label` and `detail` are `describeNotification`'s, built with `t` and
 * `locale` (English without them).
 */
export const toActivityItems = (notifications, t = englishT, locale = DEFAULT_LOCALE) =>
  (Array.isArray(notifications) ? notifications : []).map((notification) => {
    const { label, detail } = describeNotification(notification, t, locale);
    return {
      id: `notification-${notification.id}`,
      notificationId: notification.id,
      kind: notification.type,
      label,
      detail,
      at: notification.createdAt,
      read: notification.read,
      band: notification.band,
      show: notification.show,
      destination: notification.destination,
      tile: notificationTile(notification, locale),
    };
  });

/** Interleave live notifications with request/tip activity, newest first, optionally capped; `t` / `locale` as in toActivityItems. */
export const mergeActivity = (liveNotifications, activityItems, limit = Infinity, t = englishT, locale = DEFAULT_LOCALE) =>
  [...toActivityItems(liveNotifications, t, locale), ...(Array.isArray(activityItems) ? activityItems : [])]
    .sort((a, b) => b.at - a.at)
    .slice(0, limit);
